import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { FiGlobe, FiBell, FiMail } from "react-icons/fi";
import SubmitButton from "../Components/SubmitButton";
import useUserRole from "../hooks/useUserRole";
import { auth, db } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";
import toast from "react-hot-toast";

/* ---------- UI helpers (no logic) ---------- */

const Switch = ({ checked, onClick }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onClick}
    className={`relative h-7 w-[52px] shrink-0 cursor-pointer rounded-full transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-[#4268FF]/60 ${
      checked
        ? "bg-[#4268FF] shadow-[0_0_16px_rgba(66,104,255,0.45)]"
        : "bg-white/20 hover:bg-white/30"
    }`}
  >
    <span
      className={`absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-300 ease-in-out ${
        checked ? "translate-x-6" : "translate-x-0"
      }`}
    />
  </button>
);

const ToggleCard = ({ icon: Icon, text, checked, onClick }) => (
  <div
    onClick={onClick}
    className={`flex cursor-pointer flex-col justify-between gap-6 rounded-[18px] border p-5 text-start transition-all duration-200 ${
      checked
        ? "border-[#4268FF]/50 bg-[#4268FF]/10"
        : "border-white/20 bg-white/[0.06] hover:border-white/30"
    }`}
  >
    <div className="flex items-start justify-between gap-4">
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${
          checked ? "bg-[#4268FF]/20 text-[#4268FF]" : "bg-white/10 text-white/60"
        }`}
      >
        <Icon className="text-xl" />
      </div>
      {/* stopPropagation so the card click and the switch click don't double-toggle */}
      <div onClick={(e) => e.stopPropagation()}>
        <Switch checked={checked} onClick={onClick} />
      </div>
    </div>
    <p className="text-[13px] font-normal leading-snug text-white md:text-[14px]">
      {text}
    </p>
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-start text-[13px] font-medium uppercase tracking-wider text-white/60">
    {children}
  </h3>
);

/* ---------- Component ---------- */

const Preferences = () => {
  const [isToggledNotification, setIsToggledNotification] = useState(false);
  const [isToggledNewsletter, setIsToggledNewsletter] = useState(false);
  const [currentError, setCurrentError] = useState("");
  const { user } = useUserRole();
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    getValues,
    setValue,
  } = useForm();

  useEffect(() => {
    let unsubscribe;
    onAuthStateChanged(auth, (user) => {
      const userRef = doc(db, "users", user?.uid); // Adjust collection path accordingly
      unsubscribe = onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data().preferences || {};
          setValue("language", data?.language || "");
          setIsToggledNotification(data?.notification || false);
          setIsToggledNewsletter(data?.newsletter || false);
        }
      });
    });

    return () => unsubscribe();
  }, []);

  const handleValidation = () => {
    const values = getValues();
    clearErrors();
    setCurrentError("");

    if (!values.language) {
      setError("language", { type: "required", message: "La langue est requise" });
      setCurrentError("La langue est requise");
      return false;
    }

    return true;
  };

  const onSubmit = async (data) => {
    const userRef = doc(db, "users", user?.uid);
    try {
      await updateDoc(userRef, {
        preferences: {
          language: data.language,
          notification: isToggledNotification,
          newsletter: isToggledNewsletter,
        },
      });
      toast.success("Préférences mises à jour", {
        position: "top-center",
      });
    } catch (error) {
      toast.error(error, {
        position: "top-center",
      });
    }
  };

  const handleToggleNotification = () => {
    setIsToggledNotification(!isToggledNotification);
  };

  const handleToggleNewsletter = () => {
    setIsToggledNewsletter(!isToggledNewsletter);
  };

  return (
    <div className="flex w-full flex-col items-start">
      {currentError && (
        <div className="mb-4 flex w-full items-center gap-3 rounded-[15px] border border-red-500/30 bg-red-500/10 px-4 py-3 text-[13px] text-red-200 md:w-fit md:text-[14px]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="shrink-0"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M13.5356 12.65C13.78 12.8937 13.78 13.2938 13.5356 13.5375C13.2919 13.7813 12.8956 13.7813 12.6513 13.5375L10.0037 10.8875L7.33749 13.5562C7.09124 13.8 6.69252 13.8 6.44627 13.5562C6.20064 13.3062 6.20064 12.9063 6.44627 12.6625L9.11251 9.99374L6.465 7.35001C6.22062 7.10626 6.22062 6.70623 6.465 6.46248C6.70812 6.21873 7.10437 6.21873 7.34875 6.46248L9.99626 9.11247L12.6825 6.42502C12.9287 6.18127 13.3269 6.18127 13.5731 6.42502C13.8187 6.67502 13.8187 7.06873 13.5731 7.31873L10.8875 10.0063L13.5356 12.65ZM10 0C4.47688 0 0 4.475 0 10C0 15.525 4.47688 20 10 20C15.5231 20 20 15.525 20 10C20 4.475 15.5231 0 10 0Z"
              fill="#F87171"
            />
          </svg>
          <span>{currentError}</span>
        </div>
      )}

      <form
        onSubmit={handleSubmit((data) => {
          if (handleValidation()) {
            onSubmit(data);
          }
        })}
        className="w-full"
      >
        <div className="flex flex-col gap-8 md:py-2">
          {/* Language — full width */}
          <section className="flex flex-col gap-3">
            <SectionTitle>Langue</SectionTitle>
            <div className="group relative">
              <FiGlobe className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-white/60 transition-colors duration-200 group-focus-within:text-[#4268FF]" />
              <input
                type="text"
                id="language"
                placeholder="Français"
                {...register("language")}
                className={`box-border w-full rounded-[15px] border-2 bg-white/[0.08] py-3 pl-12 pr-4 text-[14px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_20px_rgba(0,0,0,0.25)] outline-none transition-all duration-200 placeholder:text-[13px] placeholder:font-normal placeholder:text-white/50 md:py-4 md:text-[15px] md:placeholder:text-[14px] hover:border-white/40 focus:border-[#4268FF] focus:bg-white/[0.12] focus:shadow-[0_0_0_4px_rgba(66,104,255,0.2)] ${
                  errors.language ? "border-red-500/70" : "border-white/25"
                }`}
              />
            </div>
          </section>

          <div className="h-px w-full bg-white/10" />

          {/* Notifications — two cards side by side */}
          <section className="flex flex-col gap-3">
            <SectionTitle>Notification</SectionTitle>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <ToggleCard
                icon={FiBell}
                text="Recevoir les notifications de la newsletter"
                checked={isToggledNotification}
                onClick={handleToggleNotification}
              />
              <ToggleCard
                icon={FiMail}
                text="Recevoir les dernières nouveautés de la communauté par mail"
                checked={isToggledNewsletter}
                onClick={handleToggleNewsletter}
              />
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 flex justify-end border-t border-white/10 pt-6">
          <SubmitButton
            buttonText="Confirmer les changements"
            type="submit"
            className="mt-0"
          />
        </div>
      </form>
    </div>
  );
};

export default Preferences;